import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Alert, Collapse, Empty, Layout } from 'antd'
import TracpatchExerciseSettingsForm from '../../component/settings/tracpatch.exercise.settings.form'
import ProgressTestGoalsSettingsForm from '../../component/settings/progress.test.goals.settings.form'
import DefaultCarePlanSettingsForm from '../../component/settings/default.care.plan.settings.form'
import RemindersManager from '../../component/reminders/reminders.manager'
import ApolloClientService from '../../services/apolloclient.service'

const { Panel } = Collapse
const { Content } = Layout
const apolloService = new ApolloClientService()

// The company's DEFAULT care plan: the same four panels as Clinic Settings,
// one level up. These are the templates Customer.addCustomer copies into every
// NEW clinic (devices, questionnaires, exercise regimen, TracPatch settings,
// Progress Test Goals, reminders). A clinic that already exists keeps its own
// settings — editing here changes what the next clinic starts from, nothing
// else. One plan per company today, typed knee / hip through the dropdown.
function CompanySettings(props) {
  const companyid = props.company && props.company._id
  const [activeKeys, setActiveKeys] = useState([])
  const collapse = (key) => setActiveKeys((ks) => ks.filter((k) => k !== key))

  // "Applies to" options for the reminders editor: every active surgery type,
  // as the company Reminders page does (a company has no clinic whitelist).
  const [surgeryTypes, setSurgeryTypes] = useState([])
  useEffect(() => {
    let cancelled = false
    apolloService
      .getActiveSurgeryTypes()
      .then((types) => {
        if (cancelled) return
        setSurgeryTypes(
          (types || [])
            .filter((st) => st && st.active !== false && st.name)
            .map((st) => String(st.name).toLowerCase())
        )
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  if (!companyid) {
    return (
      <Content style={{ padding: 24 }}>
        <Empty description='No company in this session.' />
      </Content>
    )
  }

  return (
    <Content style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 8 }}>Company Care Plan</h2>
      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 16 }}
        message='New clinics are created from this plan.'
        description={
          'Every clinic added under ' +
          (props.company.name || 'this company') +
          ' starts with these devices, exercises, questionnaires, TracPatch settings, ' +
          'Progress Test Goals and reminders as its own care plan. Clinics that already ' +
          'exist keep their settings — change those from the clinic’s own Settings page.'
        }
      />
      {/* destroyInactivePanel: a collapsed panel unmounts, so its form reloads
          fresh from the server on reopen — Cancel = collapse = discard drafts,
          exactly as on the clinic page. */}
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
        destroyInactivePanel
      >
        <Panel header='Default Care Plan' key='default-care-plan'>
          <DefaultCarePlanSettingsForm
            companyid={companyid}
            onSaved={() => collapse('default-care-plan')}
            onCancel={() => collapse('default-care-plan')}
          />
        </Panel>
        <Panel header='Reminders' key='reminders'>
          <RemindersManager
            scope='company'
            companyid={companyid}
            surgeryTypes={surgeryTypes}
            companyName={props.company.name}
            onCancel={() => collapse('reminders')}
          />
        </Panel>
        <Panel header='TracPatch Exercise Settings' key='tracpatch-exercises'>
          <TracpatchExerciseSettingsForm
            companyid={companyid}
            onSaved={() => collapse('tracpatch-exercises')}
            onCancel={() => collapse('tracpatch-exercises')}
          />
        </Panel>
        <Panel header='Progress Test Goals' key='progress-test-goals'>
          <ProgressTestGoalsSettingsForm
            companyid={companyid}
            onSaved={() => collapse('progress-test-goals')}
            onCancel={() => collapse('progress-test-goals')}
          />
        </Panel>
      </Collapse>
    </Content>
  )
}

const mapStateToProps = (state) => ({
  company: state.profile.company,
})

export default connect(mapStateToProps)(CompanySettings)
